import { repeat } from 'ramda';
import memoizeOne from 'memoize-one';

export const getSubstringBetween = (
  str: string,
  textStart: string,
  textEnd: string
): string => {
  const indexStart = str.indexOf(textStart);

  // textStart does not exist in string
  if (indexStart === -1) {
    return '';
  }

  const valueIndexStart = indexStart + textStart.length;

  return str.substring(valueIndexStart, str.indexOf(textEnd, valueIndexStart));
};

/**
 * Checks if all and only all elements from array a exists in array b, element order doesnt matter
 * @param a
 * @param b
 */
export const exactlyAllAsExistsInB = <T extends string | number>(
  a: T[],
  b: T[]
): boolean =>
  a.length === b.length && a.filter(v => b.includes(v)).length === b.length;

/**
 * Returns todays date in the format: YYYY-MM-DD
 * Don't use to send dates programatically, does not work in IE11 (use getTodaysDateUnformatted)
 */
export const getTodaysDate = (): string => {
  return new Date().toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Returns todays date in format: YYYY-(M?)M-(D?)D
 */
export const getTodaysDateUnformatted = (): string => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1;
  const date = now.getUTCDate();

  return `${year}-${month < 10 ? '0' + month : month}-${date}`;
};

/**
 * Return array with elements in random order (Fisher-Yates shuffle)
 */
export const shuffle = <T>(array: T[]): T[] => {
  let currentIndex = array.length;
  let randomIndex = array.length;
  let temporaryValue;

  while (0 !== currentIndex) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }

  return array;
};

/** Returns true if run in Internet Explorer */
export const isInternetExplorer = (): boolean =>
  /*@cc_on!@*/ false || !!(document as any).documentMode;

export const checkTrailingSlash = (path: string): string =>
  path.endsWith('/') ? path : path.concat('/');

/** Pad end of array if shorter than length */
export const padArrayEnd = <T>(item: T, length: number, list: T[]): T[] =>
  length <= list.length
    ? list
    : list.concat(repeat(item, length - list.length));

export const curriedPadArrayEnd: <T>(
  item: T
) => (length: number) => (list: T[]) => T[] = item => length => list =>
  padArrayEnd(item, length, list);

const alwaysEqual = () => true;
export const initConst: <T>(initializer: () => T) => () => T = initializer =>
  memoizeOne(initializer, alwaysEqual);

export const notNullOrUndefined = <T extends {}>(
  value: T | null | undefined
): value is T => {
  return value != null;
};

export const enumFromStringValue = <T>(
  enm: { [s: string]: T },
  value: string | undefined
): T | undefined => {
  return value !== undefined &&
    (Object.values(enm) as unknown as string[]).includes(value)
    ? (value as unknown as T)
    : undefined;
};

/**
 * Returns true if date is inbetween the two dates provided
 * @param {Date} fromDate - The date to check from
 * @param {Date} toDate - The date to check to
 * @param {Date} dateToCheck - The date to check
 * @returns {Boolean}
 */
export const inDateInterval = (
  fromDate: Date,
  toDate: Date,
  dateToCheck: Date
): boolean => {
  // Om datumet ligger mellan från- och till-datum
  return fromDate <= dateToCheck && toDate >= dateToCheck;
};
